import { createElement, ReactElement } from 'react';
import { formatDuration, intervalToDuration } from 'date-fns';
import { get, isNull, isUndefined } from 'lodash-es';
import { FieldValues } from 'react-hook-form';
import { matchPath } from 'react-router';
import { match } from 'ts-pattern';
import { ArraySchema, MixedSchema, Schema, StringSchema } from 'yup';

import { APPLICATION_ROUTES } from '~/core/application/routing/routes';
import { Category } from '../types/models';
import { TenantCustomComponentContent } from '../types/tenants';
import { Nullable, Option, OptionGroup } from '../types/utility';
import { AnyDocument, TransformFunc } from '../types/workflows';

const fractionDigits = 2;

// removes IEEE 754 noise from decimal calculations, e.g. 0.1 + 0.2 = 0.30000000000000004
export const sanitizePrecision = (value: number): number =>
  parseFloat(value.toPrecision(12));

export const upperCaseFirstCharacter = (value: string): string =>
  `${value.charAt(0).toUpperCase()}${value.slice(1)}`;

export const applyTransformFuncs = (
  initialValue: any,
  transformFuncs: TransformFunc[],
): any =>
  transformFuncs.reduce(
    (value, func) => (isUndefined(value) || isNull(value) ? null : func(value)),
    initialValue,
  );

export const centsToDollarFormat = (value: number): string =>
  sanitizePrecision(value / 100).toLocaleString('en-AU', {
    currency: 'AUD',
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: fractionDigits,
    style: 'currency',
  });

// TODO: DANGER! this looks insecure, find alternative
export const evaluate = (expression: string): any =>
  // eslint-disable-next-line no-new-func
  Function(`return ${expression}`)();

export const findOptionByValue = <T = unknown>(
  options: Option<T>[],
  value: T,
  id?: T,
): Option<T> | undefined => {
  if (id) {
    return options.find((option) => option.id === id);
  }

  return options.find((option) => option.value === value);
};

export const isObject = <T = Record<string, unknown>>(
  value: unknown,
): value is T =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

type GetObjectKeys = <T extends Record<string, unknown>>(
  obj: T,
) => Array<keyof T>;
export const getObjectKeys = Object.keys as GetObjectKeys;

const isUuid = (value: string): boolean =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

export const responsiveValue = (
  values: (number | string)[],
  breakpoints: string[] = ['base', 'lg'],
): Record<string, number | string> =>
  breakpoints.reduce(
    (object, breakpoint, idx) => ({
      ...object,
      [breakpoint]: values[idx],
    }),
    {},
  );

export const isFalseyStringOfBoolean = (value: unknown): boolean =>
  ['false', '0'].includes(String(value).toLowerCase());

export const isTruthyStringOfBoolean = (value: unknown): boolean =>
  ['true', '1'].includes(String(value).toLowerCase());

export const stringToNumber = (numberString: string | number): number => {
  if (typeof numberString === 'number') {
    return numberString;
  }

  // Remove commas and spaces, then convert
  const cleaned = numberString.replace(/[, \s]/g, '');
  const result = Number(cleaned);

  // Optionally handle invalid results gracefully
  return Number.isNaN(result) ? 0 : result;
};

export const toPercent = (value: string | number): string => {
  const numberValue = parseFloat((+value * 100).toPrecision(4)) / 1;
  return `${numberValue}%`;
};

const shouldTransformKey = (key: string): boolean => !isUuid(key);

export const transformObjectKeys = <T extends object>(
  object: T,
  func: (key: string) => string,
  mode: 'deep' | 'shallow' = 'deep',
): T => {
  const handler = (value: unknown): unknown =>
    match(value)
      .when(
        (v) => isObject(v),
        (v) => transformObjectKeys(v as object, func, 'deep'),
      )
      .when(
        (v) => Array.isArray(v),
        (v) => (v as unknown as any[]).map(handler),
      )
      .otherwise((v) => v);

  return Object.keys(object).reduce((newObject, key) => {
    const value = object[key as keyof T];
    return {
      ...newObject,
      [shouldTransformKey(key) ? func(key) : key]:
        mode === 'deep' ? handler(value) : value,
    };
  }, {} as T);
};

export const categoryToDropdown = (
  category: Category,
  includeMeta = false,
): OptionGroup<string>[] =>
  category.childCategories?.map(
    (childCategory: Category) =>
      ({
        label: childCategory.title,
        options: childCategory.childCategories?.map((sub: Category) => {
          const option: any = {
            label: sub.title,
            value: sub.title, // @TODO: should be switched to value?
          };

          if (includeMeta) {
            option.meta = sub.meta;
          }

          return option;
        }),
      }) as OptionGroup<string>,
  ) || [];

export const yupSchemaIsMixed = (schema: Schema): schema is MixedSchema =>
  // eslint-disable-next-line no-underscore-dangle
  schema._type === 'mixed';

export const yupSchemaIsString = (
  schema: Schema,
  // eslint-disable-next-line no-underscore-dangle
): schema is StringSchema<string> => schema._type === 'string';

export const yupSchemaIsArray = (
  schema: Schema,
): schema is ArraySchema<string[] | undefined, object> =>
  // eslint-disable-next-line no-underscore-dangle
  schema._type === 'array';

export const camelToSnakeCase = (str: string) =>
  str.replace(/[A-Z]/g, (letter: string) => `_${letter.toLowerCase()}`);

export const buildComponent = (
  args: TenantCustomComponentContent,
  props?: any,
): ReactElement | undefined => {
  const componentProps = {
    ...(props || {}),
    ...args.componentProps,
  };
  if (args.renderComponent) {
    return args.renderComponent(componentProps);
  }
  if (args.component) {
    return createElement(args.component, componentProps);
  }
  return undefined;
};

export const timeAgo = (dateTimeOrUnixMilliseconds: any): string | number => {
  let clonedTime = dateTimeOrUnixMilliseconds;
  switch (typeof clonedTime) {
    case 'number':
      break;
    case 'string':
      clonedTime = +new Date(clonedTime);
      break;

    default:
      clonedTime = +new Date();
  }
  const timeFormats: Array<[number, string, number | string]> = [
    [60, 'seconds', 1], // 60
    [120, '1 minute ago', '1 minute from now'], // 60*2
    [3600, 'minutes', 60], // 60*60, 60
    [7200, '1 hour ago', '1 hour from now'], // 60*60*2
    [86400, 'hours', 3600], // 60*60*24, 60*60
    [172800, 'Yesterday', 'Tomorrow'], // 60*60*24*2
    [604800, 'days', 86400], // 60*60*24*7, 60*60*24
    [1209600, 'Last week', 'Next week'], // 60*60*24*7*4*2
    [2419200, 'weeks', 604800], // 60*60*24*7*4, 60*60*24*7
    [4838400, 'Last month', 'Next month'], // 60*60*24*7*4*2
    [29030400, 'months', 2419200], // 60*60*24*7*4*12, 60*60*24*7*4
    [58060800, 'Last year', 'Next year'], // 60*60*24*7*4*12*2
    [2903040000, 'years', 29030400], // 60*60*24*7*4*12*100, 60*60*24*7*4*12
    [5806080000, 'Last century', 'Next century'], // 60*60*24*7*4*12*100*2
    [58060800000, 'centuries', 2903040000], // 60*60*24*7*4*12*100*20, 60*60*24*7*4*12*100
  ];

  let seconds = sanitizePrecision((Date.now() - clonedTime) / 1000);
  let token = 'ago';

  if (seconds === 0) {
    return 'Just now';
  }
  if (seconds < 0) {
    seconds = Math.abs(seconds);
    token = 'from now';
  }
  for (let i = 0; i < timeFormats.length; i += 1) {
    const format = timeFormats[i];
    if (seconds < format[0]) {
      if (typeof format[2] === 'string') {
        return format[1] as string;
      }
      return `${Math.floor(seconds / (format[2] as number))} ${format[1]} ${token}`;
    }
  }
  return clonedTime;
};

const isValidDate = (d: unknown): boolean =>
  d instanceof Date && !Number.isNaN(d.getTime());

export const calculateTimeInYearsAndMonths = (
  date: Date,
  includeMonths: boolean = true,
): string => {
  if (!isValidDate(date)) return '';

  const props = {
    end: new Date(date.getFullYear(), date.getMonth(), date.getDate()),
    start: new Date(),
  };

  const { years, months } = intervalToDuration(props);

  const formatted = formatDuration({
    years,
    ...(includeMonths ? { months } : {}),
  });

  return formatted || 'this month';
};

export const getWorkflowFromUrl = (): string | undefined => {
  const { pathname } = window.location;

  if (
    !matchPath(APPLICATION_ROUTES.workflowStep.path, pathname) &&
    !matchPath(APPLICATION_ROUTES.workflowStepPlace.path, pathname)
  ) {
    return undefined;
  }

  const matches = pathname.match(/workflow\/(.+?)(\/|$)/);

  if (matches !== null) {
    return matches[1];
  }

  return undefined;
};

export const getWorkflowFromPath = (
  config: any,
  accessorKey: string,
): string | undefined => {
  const normalise = (s: string) => s?.replace(/\W/g, '_').toLowerCase();

  const { pathname } = window.location;
  const symbols = Object.keys(config);
  const candidates = pathname.split('/').map(normalise);

  for (const symbol of symbols) {
    if (
      candidates.some(
        (candidate) => candidate && normalise(symbol).startsWith(candidate),
      )
    ) {
      return config[symbol][accessorKey];
    }
  }

  return undefined;
};

export const camelize = (str: string) =>
  str
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) =>
      index === 0 ? word.toLowerCase() : word.toUpperCase(),
    )
    .replace(/\s+/g, '');

export const capitalizeFirstLetter = (string: string): string =>
  string.charAt(0).toUpperCase() + string.slice(1);

export const multiselectToCommaSeparatedString =
  (keyPrefix: string, options: Option<string>[]) =>
  (document: AnyDocument | undefined) => {
    const values = options
      .filter(({ value }) => {
        const documentValue = get(
          document,
          `${keyPrefix}${capitalizeFirstLetter(value)}`,
        );
        return documentValue === true;
      })
      .map(({ label }) => label);

    return values.join(', ') || '-';
  };

function isEmptyString(stringToCheck: any, ignoreSpaces = true) {
  if (typeof stringToCheck === 'string') {
    return (ignoreSpaces ? stringToCheck.trim() : stringToCheck) === '';
  }

  return false;
}

export function notNullOrUndefined<T>(val: Nullable<T> | undefined): val is T {
  return val !== null && typeof val !== 'undefined';
}

export function notNullOrUndefinedOrEmpty<T>(
  val: Nullable<T> | undefined | '',
): val is T {
  return (
    val !== null && typeof val !== 'undefined' && !isEmptyString(val as T | '')
  );
}

export const dirtyValues = (
  dirtyFields: any,
  allValues: FieldValues,
): FieldValues => {
  if (dirtyFields === true || Array.isArray(dirtyFields)) {
    return allValues;
  }

  return Object.fromEntries(
    Object.keys(dirtyFields).map((key) => [
      key,
      dirtyValues(dirtyFields[key], allValues[key]),
    ]),
  );
};

export function isLockedApplication(hooks: any[]) {
  const lockedHookIds = [
    'documentFormUpdate',
    'updateDocumentViaForm',
    'runCalculator',
    'documentUpdate',
    'documentStore',
    'docStore',
    'envelopeStore',
    'messageStore',
  ];

  return lockedHookIds.every((id) => {
    const hook = hooks.find((h) => h.id === id);
    return hook?.status === 'deny';
  });
}

export function isDeniedHook(hooks: any[], id: string) {
  const hook = hooks.find((h) => h.id === id);
  return hook?.status === 'deny';
}

export const stringArrayToCommaSeparatedDisplay = (input: string[]): string => {
  if (input.length > 0) {
    if (input.length > 2) {
      const items = input.slice(0, -1);
      const lastItem = input.slice(-1)[0];

      return `${items.join(', ')} and ${lastItem}`;
    }
    return input.join(' and ');
  }
  return '';
};
